import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Clock, Calendar, CheckCircle2, ChevronRight, Activity, Users, DollarSign, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import CsvExportButton from '@/components/CsvExportButton';
import ClickUpTaskButton from '@/components/ClickUpTaskButton';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const SupportClients = () => {
    const navigate = useNavigate();
    const [period, setPeriod] = useState('this_month');
    const [stats, setStats] = useState({
        total_clients: 0,
        total_contract_hours: 0,
        total_monthly_amount: 0,
        maintenance_completed_count: 0
    });
    const [clients, setClients] = useState([]);
    const [paidTasks, setPaidTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('name');

    useEffect(() => {
        fetchClients();
    }, [period]);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token');
        return {
            headers: { Authorization: `Bearer ${token}` }
        };
    };

    const getPeriodDates = (p) => {
        const now = new Date();
        let start = new Date();
        let end = new Date();

        switch (p) {
            case 'this_month':
                // Use UTC to avoid timezone issues where local start time > UTC report time (00:00)
                start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
                end = new Date(); // Now
                break;
            case 'last_month':
                start = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1));
                end = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 0, 23, 59, 59));
                break;
            case 'last_3_months':
                start = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 3, 1));
                end = new Date();
                break;
            case 'ytd':
                start = new Date(Date.UTC(now.getFullYear(), 0, 1));
                end = new Date();
                break;
            case 'last_year':
                start = new Date(Date.UTC(now.getFullYear() - 1, 0, 1));
                end = new Date(Date.UTC(now.getFullYear() - 1, 11, 31, 23, 59, 59));
                break;
            default: // this_month
                start = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
        }
        return { start: start.toISOString(), end: end.toISOString() };
    };

    const fetchClients = async () => {
        setLoading(true);
        const { start, end } = getPeriodDates(period);
        try {
            const response = await axios.get(`${API}/admin/support-clients`, {
                ...getAuthHeaders(),
                params: { start_date: start, end_date: end }
            });
            // Handle new response structure
            if (response.data.clients) {
                setClients(response.data.clients);
                setStats(response.data.summary);
                setPaidTasks(response.data.paid_tasks || []);
            } else {
                // Fallback for old API if needed
                setClients(response.data);
            }
        } catch (error) {
            console.error('Error fetching support clients:', error);
            toast.error('Failed to load support clients');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate('/dashboard')}
                                className="gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" /> Dashboard
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate('/admin')}
                                className="gap-2"
                            >
                                Admin Panel
                            </Button>
                            <div className="h-6 w-px bg-gray-200 mx-2"></div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                    <Users className="w-5 h-5" />
                                    Support Clients
                                </h1>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <CsvExportButton
                                filename={`support-clients-${period}`}
                                rows={clients}
                                columns={[
                                    { label: 'Client', value: row => row.name || row.company_name || row.email },
                                    { label: 'Email', value: 'email' },
                                    { label: 'Contract Hours', value: row => row.monthly_hours || row.contract_hours || 0 },
                                    { label: 'Used Hours', value: row => row.used_hours || row.hours_used || 0 },
                                    { label: 'Active Tickets', value: row => row.active_tickets || 0 },
                                    { label: 'Maintenance Completed', value: row => row.maintenance_completed || row.maintenance_completed_count || 0 },
                                ]}
                            />
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-[180px] h-9">
                                    <SelectValue placeholder="Sort By..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="name">Sort by Name</SelectItem>
                                    <SelectItem value="tickets">Sort by Active Tickets</SelectItem>
                                    <SelectItem value="hours">Sort by Used Hours</SelectItem>
                                </SelectContent>
                            </Select>

                            <div className="flex flex-wrap items-center bg-gray-100 rounded-lg p-1">
                                <Button
                                    variant={period === 'this_month' ? 'default' : 'ghost'}
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => setPeriod('this_month')}
                                >
                                    This Month
                                </Button>
                                <Button
                                    variant={period === 'last_month' ? 'default' : 'ghost'}
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => setPeriod('last_month')}
                                >
                                    Last Month
                                </Button>
                                <Button
                                    variant={period === 'last_3_months' ? 'default' : 'ghost'}
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => setPeriod('last_3_months')}
                                >
                                    3 Months
                                </Button>
                                <Button
                                    variant={period === 'ytd' ? 'default' : 'ghost'}
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => setPeriod('ytd')}
                                >
                                    YTD
                                </Button>
                                <Button
                                    variant={period === 'last_year' ? 'default' : 'ghost'}
                                    size="sm"
                                    className="text-xs h-7"
                                    onClick={() => setPeriod('last_year')}
                                >
                                    Last Year
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <Card>
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Total Active Clients</p>
                                <h3 className="text-2xl font-bold mt-1">{stats.total_clients}</h3>
                            </div>
                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Users className="w-5 h-5 text-blue-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Total Contract Hours</p>
                                <h3 className="text-2xl font-bold mt-1">{stats.total_contract_hours}h</h3>
                                <p className="text-xs text-gray-400 mt-1">Monthly Capacity</p>
                            </div>
                            <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                                <Clock className="w-5 h-5 text-green-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Monthly Revenue</p>
                                <h3 className="text-2xl font-bold mt-1">${(stats.total_monthly_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                                <p className="text-xs text-gray-400 mt-1">Combined Contracts</p>
                            </div>
                            <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-emerald-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Maintenance Completed</p>
                                <h3 className="text-2xl font-bold mt-1">{stats.maintenance_completed_count} / {stats.total_clients}</h3>
                                <p className="text-xs text-gray-400 mt-1">For Selected Period</p>
                            </div>
                            <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                                <CheckCircle2 className="w-5 h-5 text-purple-600" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <Card key={i}>
                                <CardHeader>
                                    <Skeleton className="h-6 w-3/4 mb-2" />
                                    <Skeleton className="h-4 w-1/2" />
                                </CardHeader>
                                <CardContent>
                                    <Skeleton className="h-20 w-full mb-4" />
                                    <Skeleton className="h-40 w-full" />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : clients.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-lg border border-gray-200 border-dashed">
                        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">No Support Clients</h3>
                        <p className="text-gray-500 mt-2">Mark users as "Support Client" in User Management to see them here.</p>
                        <Button className="mt-4" variant="outline" onClick={() => navigate('/admin/users')}>
                            Manage Users
                        </Button>
                    </div>
                ) : (
                    <>
                        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            Monthly Recurring Clients
                            <span className="text-sm font-normal text-gray-500 ml-1">({clients.length} client{clients.length !== 1 ? 's' : ''})</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...clients]
                                .sort((a, b) => {
                                    if (sortBy === 'name') {
                                        const nameA = a.user.company_name || a.user.email || '';
                                        const nameB = b.user.company_name || b.user.email || '';
                                        return nameA.localeCompare(nameB);
                                    } else if (sortBy === 'hours') {
                                        return (b.hours_used_period || 0) - (a.hours_used_period || 0);
                                    } else if (sortBy === 'tickets') {
                                        return (b.active_tickets_count || 0) - (a.active_tickets_count || 0);
                                    }
                                    return 0;
                                })
                                .map((clientData) => {
                                    // Extract data from new structure
                                    const { user, active_tickets_count, last_active, hours_used_period, maintenance_completed_period } = clientData;
                                    const hoursUsed = hours_used_period || 0;
                                    const usagePercent = user.contract_hours > 0
                                        ? (hoursUsed / user.contract_hours) * 100
                                        : 0;

                                    return (
                                        <Card key={user.id} className="overflow-hidden hover:shadow-md transition-shadow">
                                            <div className="p-6 bg-white flex flex-col justify-between h-full">
                                                <div>
                                                    <div className="flex items-start justify-between mb-4 gap-2">
                                                        <div className="min-w-0">
                                                            <h3 className="text-lg font-bold text-gray-900 truncate">
                                                                {user.company_name || user.email}
                                                            </h3>
                                                            {user.website_url && (
                                                                <a
                                                                    href={user.website_url.startsWith('http') ? user.website_url : `https://${user.website_url}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-sm text-blue-600 hover:underline truncate block"
                                                                >
                                                                    {user.website_url}
                                                                </a>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-col gap-1 items-end">
                                                            {maintenance_completed_period && (
                                                                <Badge className="bg-green-100 text-green-700 border-green-200 gap-1 whitespace-nowrap">
                                                                    <CheckCircle2 className="w-3 h-3" />
                                                                    Maint. Done
                                                                </Badge>
                                                            )}
                                                            <Badge variant={active_tickets_count > 0 ? "default" : "secondary"}>
                                                                {active_tickets_count} Active
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4 mt-6">
                                                        <div>
                                                            <div className="flex justify-between text-sm mb-1">
                                                                <span className="font-medium text-gray-700">Period Usage</span>
                                                                <span className="text-gray-500">{hoursUsed.toFixed(1)} / {user.contract_hours} hrs</span>
                                                            </div>
                                                            <Progress value={usagePercent} className={`h-2 ${usagePercent > 100 ? 'bg-red-100' : ''}`} indicatorClassName={usagePercent > 90 ? 'bg-red-500' : usagePercent > 75 ? 'bg-yellow-500' : 'bg-green-600'} />
                                                            {usagePercent > 100 && (
                                                                <p className="text-xs text-red-600 mt-1 font-medium">Over contract limit</p>
                                                            )}
                                                        </div>

                                                        {user.contract_amount > 0 && (
                                                            <div className="flex items-center justify-between text-sm">
                                                                <span className="font-medium text-gray-700 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Contract Value</span>
                                                                <span className="font-bold text-emerald-700">${(user.contract_amount || 0).toFixed(2)}/mo</span>
                                                            </div>
                                                        )}

                                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                                            <Activity className="w-4 h-4" />
                                                            <span>Last active: {new Date(last_active).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <Button
                                                    variant="outline"
                                                    className="w-full mt-6"
                                                    onClick={() => navigate(`/admin/users/${user.id}`)}
                                                >
                                                    View Full Dashboard <ChevronRight className="w-4 h-4 ml-1" />
                                                </Button>
                                            </div>
                                        </Card>
                                    );
                                })}
                        </div>
                    </>
                )}

                {paidTasks.length > 0 && (
                    <div className="mt-10">
                        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-emerald-600" /> Paid Support Tasks
                            <span className="text-sm font-normal text-gray-500 ml-1">({paidTasks.length} in selected period)</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {paidTasks.map(t => (
                                <div
                                    key={t.id}
                                    className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 cursor-pointer hover:bg-emerald-100 hover:border-emerald-300 hover:shadow-md transition-all"
                                    onClick={() => navigate(`/dashboard?ticketId=${t.id}`)}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="font-semibold text-gray-800 text-sm line-clamp-2">{t.title}</p>
                                        <span className="shrink-0 text-emerald-700 font-bold text-sm">${(t.paid_amount || 0).toFixed(2)}</span>
                                    </div>
                                    {t.company_name && (
                                        <p className="text-xs text-gray-500 mt-1">{t.company_name}</p>
                                    )}
                                    <div className="flex items-center justify-between mt-2">
                                        <span className="text-xs text-gray-400">{new Date(t.created_at).toLocaleDateString()}</span>
                                        <div className="flex items-center gap-2">
                                            <ClickUpTaskButton taskId={t.clickup_task_id} iconOnly className="h-7 w-7 p-0 bg-white" />
                                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">Paid Support ↗</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default SupportClients;
